import { useEffect, useMemo, useState } from 'react';
import { DriverDocuments } from '../documents';
import driverService from '../../services/driverService';
import { useAuth } from '../../contexts/AuthContext';
import ApplicationOriginCard from './applications/ApplicationOriginCard';

const toDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    // Plain dates ('1990-05-17') are calendar dates: new Date() would read them
    // as UTC midnight and show the previous day in US time zones.
    const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (plain) return new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]));
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }

  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  if (typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000);
  }

  return null;
};

const formatDate = (value, fallback = '—') => {
  const date = toDate(value);
  if (!date) {
    return fallback;
  }

  return date.toLocaleDateString();
};

const formatPhone = (value, fallback = '—') => {
  if (!value) {
    return fallback;
  }

  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return value;
};

const formatAddress = (driver) => {
  if (!driver) {
    return '—';
  }

  const parts = [driver.address, driver.city, driver.state, driver.zipCode]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);

  return parts.length ? parts.join(', ') : '—';
};

const DriverProfile = ({ driverId, initialDriver = null, onDriverUpdated, initialTab = 'personal', readOnly = false }) => {
  const normalizedInitialTab = typeof initialTab === 'string' ? initialTab : 'personal';
  const [activeTab, setActiveTab] = useState(normalizedInitialTab);
  const [driver, setDriver] = useState(initialDriver);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const { company, userData } = useAuth();
  const canReviewApplications = ['admin', 'manager', 'super_admin'].includes(userData?.role);

  const resolvedCompanyId = useMemo(() => {
    if (company?.id) return company.id;
    if (company?.companyId) return company.companyId;
    if (userData?.companyId) return userData.companyId;
    if (userData?.company?.id) return userData.company.id;
    return null;
  }, [company, userData]);

  const resolvedDriverName = useMemo(() => {
    if (!driver) {
      return 'Driver';
    }

    const nameFromParts = [driver.firstName, driver.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return driver.name?.trim() || nameFromParts || driver.driverId || driver.id || 'Driver';
  }, [driver]);

  useEffect(() => {
    setDriver(initialDriver);
  }, [initialDriver]);

  useEffect(() => {
    const nextTab = typeof initialTab === 'string' ? initialTab : 'personal';
    setActiveTab(nextTab);
    setStatusError(null);
    setStatusUpdating(false);
  }, [driverId, initialTab]);

  useEffect(() => {
    let isCancelled = false;

    const fetchDriver = async () => {
      if (!driverId || !resolvedCompanyId) {
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const fetchedDriver = await driverService.getDriverById(driverId, { companyId: resolvedCompanyId });
        if (!isCancelled) {
          setDriver(fetchedDriver);
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error('Failed to load driver profile:', error);
        const friendly = typeof error?.getUserFriendlyMessage === 'function'
          ? error.getUserFriendlyMessage()
          : (error?.message || 'Failed to load driver details.');
        setLoadError(friendly);
        setDriver(null);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchDriver();

    return () => {
      isCancelled = true;
    };
  }, [driverId, resolvedCompanyId]);

  const handleStatusChange = async (nextStatus) => {
    if (readOnly) {
      return;
    }

    if (!driver?.id || !resolvedCompanyId || statusUpdating) {
      return;
    }

    const normalizedNextStatus = typeof nextStatus === 'string'
      ? nextStatus.toLowerCase()
      : 'active';

    const currentStatus = typeof driver?.status === 'string'
      ? driver.status.toLowerCase()
      : (driver?.isActive ? 'active' : 'inactive');

    if (normalizedNextStatus === currentStatus) {
      return;
    }

    setStatusUpdating(true);
    setStatusError(null);

    try {
      const updatedDriver = await driverService.updateDriverStatus(driver.id, normalizedNextStatus, {
        companyId: resolvedCompanyId
      });

      setDriver(updatedDriver);
      if (typeof onDriverUpdated === 'function') {
        onDriverUpdated(updatedDriver);
      }
    } catch (error) {
      console.error('Failed to update driver status:', error);
      const friendly = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Unable to update driver status.');
      setStatusError(friendly);
    } finally {
      setStatusUpdating(false);
    }
  };

  const tabs = [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'employment', name: 'Employment', icon: '💼' },
    { id: 'compliance', name: 'Compliance History', icon: '✅' },
    { id: 'documents', name: 'Documents', icon: '📄' }
  ];

  const getStatusColor = (status) => {
    if (typeof status !== 'string') {
      return 'text-gray-600 bg-gray-100';
    }

    switch (status.toLowerCase()) {
      case 'valid': case 'pass': case 'attended': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'expired': case 'fail': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderPersonalInfo = () => {
    if (!driver) {
      return (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-sm text-gray-500">Driver details are unavailable.</p>
        </div>
      );
    }

    const medicalExpirationRaw = driver.medicalExpirationDate || driver.medicalCertExpiration;
    const medicalExpirationDate = toDate(medicalExpirationRaw);
    const today = new Date();

    const medicalBadge = (() => {
      if (!medicalExpirationDate || Number.isNaN(medicalExpirationDate.getTime())) {
        return { className: 'bg-gray-100 text-gray-700', label: 'Unknown' };
      }

      const daysUntilExpiry = Math.ceil((medicalExpirationDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry < 0) {
        return { className: 'bg-red-100 text-red-800', label: 'Expired' };
      }
      if (daysUntilExpiry < 30) {
        return { className: 'bg-red-100 text-red-800', label: `${daysUntilExpiry} days left` };
      }
      if (daysUntilExpiry < 60) {
        return { className: 'bg-yellow-100 text-yellow-800', label: `${daysUntilExpiry} days left` };
      }
      return { className: 'bg-green-100 text-green-800', label: 'Valid' };
    })();

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Basic Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Full Name</label>
                <p className="mt-1 text-sm text-gray-900 font-medium break-words">{resolvedDriverName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Employee ID</label>
                <p className="mt-1 text-sm text-gray-900 font-medium">{driver.employeeId || driver.driverId || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1 text-sm text-gray-900 font-medium">
                  {driver.phone ? (
                    <a href={`tel:${driver.phone}`} className="text-blue-600 hover:text-blue-800">
                      {formatPhone(driver.phone)}
                    </a>
                  ) : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900 font-medium break-all">
                  {driver.email ? (
                    <a href={`mailto:${driver.email}`} className="text-blue-600 hover:text-blue-800">
                      {driver.email}
                    </a>
                  ) : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address</label>
                <p className="mt-1 text-sm text-gray-900 font-medium break-words">{formatAddress(driver)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              License Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">CDL Number</label>
                <p className="mt-1 text-sm text-gray-900 font-mono">{driver.cdlNumber || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">CDL Class</label>
                <p className="mt-1 text-sm text-gray-900">{driver.cdlClass || driver.licenseClass || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">CDL Issue Date</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(driver.cdlIssueDate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">CDL Expiration</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(driver.cdlExpirationDate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Medical Cert Expiration</label>
                <div className="mt-1 flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                  <p className="text-sm text-gray-900 font-medium">{formatDate(medicalExpirationRaw)}</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${medicalBadge.className}`}>
                    {medicalBadge.label}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Endorsements</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {driver.hazmatEndorsement && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">HAZMAT</span>
                  )}
                  {driver.passengerEndorsement && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Passenger</span>
                  )}
                  {driver.schoolBusEndorsement && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">School Bus</span>
                  )}
                  {driver.doubleTripleEndorsement && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Double/Triple</span>
                  )}
                  {(!driver.hazmatEndorsement && !driver.passengerEndorsement && !driver.schoolBusEndorsement && !driver.doubleTripleEndorsement) && (
                    <span className="text-sm text-gray-500">None</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const renderComplianceHistory = () => {
    const complianceHistory = Array.isArray(driver?.complianceHistory)
      ? driver.complianceHistory
      : [];

    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance History</h3>
        {complianceHistory.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
            No compliance history recorded yet.
          </div>
        ) : (
          <div className="space-y-4">
            {complianceHistory.map((record) => (
              <div key={record.id || record.date} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900">{record.type || 'Compliance Event'}</span>
                    {record.result && (
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(record.result)}`}>
                        {record.result}
                      </span>
                    )}
                  </div>
                  {record.date && (
                    <span className="text-sm text-gray-500">{formatDate(record.date)}</span>
                  )}
                </div>
                {record.notes && (
                  <p className="text-sm text-gray-600">{record.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  const renderEmployment = () => {
    if (!driver) {
      return null;
    }

    const hireDate = toDate(driver.hireDate || driver.originalHireDate);
    const yearsOfService = hireDate && !Number.isNaN(hireDate.getTime())
      ? Math.max(0, Math.floor((Date.now() - hireDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)))
      : null;

    const statusLabelRaw = driver.status || (driver.isActive ? 'active' : 'inactive');
    const normalizedStatus = typeof statusLabelRaw === 'string' ? statusLabelRaw.toLowerCase() : 'unknown';
    const statusBadgeClass = normalizedStatus === 'inactive'
      ? 'bg-red-100 text-red-700'
      : 'bg-green-100 text-green-800';
    const isActive = normalizedStatus !== 'inactive';

    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-8 0h8m-8 0a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2V8a2 2 0 00-2-2" />
            </svg>
            Employment Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">{formatDate(driver.dateOfBirth)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Hire Date</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">{formatDate(driver.hireDate || driver.originalHireDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Years of Service</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">
                {yearsOfService !== null ? `${yearsOfService} years` : '—'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Employee Status</label>
              <div className="mt-1 space-y-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass}`}>
                  {statusLabelRaw ? statusLabelRaw.toString().replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) : 'Unknown'}
                </span>
                {!readOnly && (
                  <>
                    <div className="inline-flex rounded-md shadow-sm pl-2">
                      <button
                        type="button"
                        onClick={() => handleStatusChange('active')}
                        disabled={statusUpdating || !driver?.id || !resolvedCompanyId}
                        className={`px-4 py-2 text-sm font-medium border ${
                          isActive
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400'
                        } ${statusUpdating ? 'cursor-not-allowed opacity-60' : ''} rounded-l-md`}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange('inactive')}
                        disabled={statusUpdating || !driver?.id || !resolvedCompanyId}
                        className={`px-4 py-2 text-sm font-medium border ${
                          !isActive
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-red-600 border-red-200 hover:border-red-400'
                        } ${statusUpdating ? 'cursor-not-allowed opacity-60' : ''} rounded-r-md`}
                      >
                        Inactive
                      </button>
                    </div>
                    {statusError && (
                      <p className="text-sm text-red-600">{statusError}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-6">
      <DriverDocuments 
        driverId={driverId} 
        driverName={resolvedDriverName}
        driverEmail={driver?.email || ''}
        companyId={resolvedCompanyId}
      />
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal': return renderPersonalInfo();
      case 'employment': return (
        <div className="space-y-6">
          {renderEmployment()}
          {driver?.applicationId && canReviewApplications && <ApplicationOriginCard applicationId={driver.applicationId} />}
        </div>
      );
      case 'compliance': return renderComplianceHistory();
      case 'documents': return renderDocuments();
      default: return renderPersonalInfo();
    }
  };

  const renderBodyContent = () => {
    if (isLoading && !driver) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-500">
          Loading driver details...
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="bg-white border border-red-200 rounded-lg p-6">
          <p className="text-sm text-red-600">{loadError}</p>
        </div>
      );
    }

    if (!driver) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-500">
          Driver details are not available.
        </div>
      );
    }

    return renderTabContent();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Driver Header */}
      <div className="sticky top-0 z-1 bg-white border-b border-gray-200 shadow-sm">

        {/* Sticky Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2 text-base sm:text-lg">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden text-xs">{tab.name.split(' ')[0]}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Scrollable Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {renderBodyContent()}
        </div>
      </div>
    </div>
  );
};

export default DriverProfile;
